import React, { useEffect, useState } from "react";
import {
  Table,
  Avatar,
  Select,
  Spin,
  Card,
  Typography,
  Tag,
  Tooltip,
} from "antd";
import {
  UserOutlined,
  LockFilled,
  UnlockOutlined,
} from "@ant-design/icons";
import GetCycleLockListApi from "../../api/users/GetCycleLockListApi";
import UpdateUserCycleLockApi from "../../api/users/UpdateUserCycleLockApi";
import { GetAllPerfCyclesApi } from "../../api/GetAllPerfCyclesApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

function PerformanceCycleLock() {
  const [users, setUsers] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [usersRes, cyclesRes] = await Promise.all([
        GetCycleLockListApi(),
        GetAllPerfCyclesApi(),
      ]);
      if (usersRes && Array.isArray(usersRes)) {
        setUsers(usersRes);
      } else {
        openNotificationError("bottomRight", "Failed to fetch users");
      }
      if (cyclesRes?.success && Array.isArray(cyclesRes.data)) {
        setCycles(cyclesRes.data.filter((c) => c.status !== "Deleted"));
      } else {
        openNotificationError("bottomRight", "Failed to fetch performance cycles");
      }
    } catch (e) {
      openNotificationError("bottomRight", "Error loading data");
    } finally {
      setLoading(false);
    }
  }

  async function handleCycleChange(userId, cycleId) {
    setUpdating((prev) => ({ ...prev, [userId]: true }));
    try {
      const res = await UpdateUserCycleLockApi(userId, cycleId || null);
      if (res?.success) {
        setUsers((prev) =>
          prev.map((u) =>
            u.userId === userId
              ? { ...u, lockedCycleId: cycleId || null }
              : u
          )
        );
        openNotificationSuccess(
          "bottomRight",
          cycleId ? "Cycle lock applied" : "Cycle lock removed"
        );
      } else {
        openNotificationError(
          "bottomRight",
          res?.message || "Failed to update cycle lock"
        );
      }
    } catch {
      openNotificationError("bottomRight", "Error updating cycle lock");
    } finally {
      setUpdating((prev) => {
        const s = { ...prev };
        delete s[userId];
        return s;
      });
    }
  }

  const lockedCount = users.filter((u) => !!u.lockedCycleId).length;

  const columns = [
    {
      title: (
        <span style={{ fontWeight: 700, fontSize: 13 }}>User</span>
      ),
      key: "user",
      fixed: "left",
      width: 280,
      render: (_, record) => {
        const isLocked = !!record.lockedCycleId;
        const borderColor = isLocked ? "#f97316" : "#d1d5db";
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 0",
            }}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                src={record.imageUrl}
                icon={<UserOutlined />}
                size={44}
                style={{ border: `2.5px solid ${borderColor}` }}
              />
              <span
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  background: isLocked ? "#f97316" : "#9ca3af",
                  border: "2px solid #fff",
                }}
              />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#111",
                  lineHeight: "18px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {record.firstName || record.username}{" "}
                {record.lastName || ""}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#555",
                  marginTop: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {record.designationName || "—"}
                {record.unitName && (
                  <span style={{ color: "#aaa" }}> · {record.unitName}</span>
                )}
              </div>
              <div style={{ marginTop: 4 }}>
                {isLocked ? (
                  <Tag
                    icon={<LockFilled />}
                    style={{
                      margin: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "1px 7px",
                      borderRadius: 20,
                      background: "#fff7ed",
                      border: "1px solid #fdba74",
                      color: "#c2410c",
                    }}
                  >
                    Locked
                  </Tag>
                ) : (
                  <Tag
                    icon={<UnlockOutlined />}
                    style={{
                      margin: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "1px 7px",
                      borderRadius: 20,
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      color: "#6b7280",
                    }}
                  >
                    Free
                  </Tag>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <span style={{ fontWeight: 700, fontSize: 13 }}>
          Locked Performance Cycle
        </span>
      ),
      key: "lockedCycle",
      render: (_, record) => {
        const isUpdating = !!updating[record.userId];
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Select
              allowClear
              showSearch
              loading={isUpdating}
              disabled={isUpdating}
              value={record.lockedCycleId || undefined}
              placeholder={
                <span style={{ color: "#9ca3af" }}>
                  No lock — user can switch freely
                </span>
              }
              style={{ width: 340 }}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(val) => handleCycleChange(record.userId, val ?? null)}
              onClear={() => handleCycleChange(record.userId, null)}
            >
              {cycles.map((cycle) => (
                <Option
                  key={cycle.id}
                  value={cycle.id}
                  label={cycle.name}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{cycle.name}</span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexShrink: 0,
                      }}
                    >
                      <span style={{ fontSize: 11, color: "#6b7280" }}>
                        {moment(cycle.startingOn).format("DD MMM YY")} –{" "}
                        {moment(cycle.endingOn).format("DD MMM YY")}
                      </span>
                      <Tag
                        style={{
                          margin: 0,
                          fontSize: 10,
                          padding: "0 5px",
                          borderRadius: 10,
                          background:
                            cycle.status === "Active"
                              ? "#dcfce7"
                              : cycle.status === "Scheduled"
                              ? "#fef9c3"
                              : "#f3f4f6",
                          border: "none",
                          color:
                            cycle.status === "Active"
                              ? "#15803d"
                              : cycle.status === "Scheduled"
                              ? "#92400e"
                              : "#6b7280",
                        }}
                      >
                        {cycle.status}
                      </Tag>
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
            {isUpdating && <Spin size="small" />}
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6">
      <Card
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: 10, overflow: "hidden" }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #f0f0f0",
            background: "#fafafa",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Performance Cycle Lock
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Restrict a user to a single performance cycle. Locked users
              cannot switch cycles. Leave blank to allow free switching.
            </Text>
          </div>
          <Tooltip title="Users currently locked to a specific cycle">
            <Tag
              icon={<LockFilled />}
              style={{
                fontSize: 13,
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: 20,
                background: lockedCount > 0 ? "#fff7ed" : "#f9fafb",
                border: `1px solid ${lockedCount > 0 ? "#fdba74" : "#e5e7eb"}`,
                color: lockedCount > 0 ? "#c2410c" : "#6b7280",
                marginTop: 2,
                cursor: "default",
              }}
            >
              {lockedCount} locked
            </Tag>
          </Tooltip>
        </div>

        {/* Table */}
        <style>{`
          .pcl-table .ant-table-thead > tr > th {
            background: #f8fafc !important;
            border-bottom: 2px solid #e5e7eb !important;
            padding: 10px 16px !important;
          }
          .pcl-table .ant-table-tbody > tr > td {
            padding: 4px 16px !important;
            border-bottom: 1px solid #f3f4f6 !important;
          }
          .pcl-table .ant-table-tbody > tr:hover > td {
            background: #fafafa !important;
          }
        `}</style>

        <Table
          className="pcl-table"
          columns={columns}
          dataSource={users}
          rowKey="userId"
          loading={loading}
          size="small"
          scroll={{ x: "max-content" }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}–${range[1]} of ${total} users`,
            pageSizeOptions: ["10", "15", "25", "50"],
          }}
        />
      </Card>
    </div>
  );
}

export default PerformanceCycleLock;
